import { useState } from 'react'

function CopyPromptBlock({ label, prompt, sublabel }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div style={{ background: '#1A1A1A', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>
        💡 {label}
      </div>
      {sublabel && <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.75rem', lineHeight: 1.5 }}>{sublabel}</div>}
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '6px', padding: '0.85rem', marginBottom: '0.85rem', fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {prompt}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={copy} style={{ flex: 1, padding: '0.6rem', background: copied ? '#00712D' : '#fff', color: copied ? '#fff' : '#1A1A1A', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
          {copied ? '✓ Copied to clipboard!' : 'Copy prompt'}
        </button>
        <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1rem', background: '#00712D', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          Open Claude.ai →
        </a>
      </div>
    </div>
  )
}

export default function ResumeAnalyzer({ session, profile, onBack }) {
  const [resume, setResume] = useState('')
  const [jobDesc, setJobDesc] = useState('')
  const [activePrompt, setActivePrompt] = useState('ats')

  const targetTitle = profile?.target_titles?.split(',')[0]?.trim() || 'my target role'

  const PROMPTS = {
    ats: {
      label: 'ATS & Keyword Analysis',
      sublabel: 'Find out which keywords you\'re missing and how to fix them',
      prompt: `You are a senior recruiter and ATS expert. Please analyze my resume against this job description.

RESUME:
${resume || '[PASTE YOUR RESUME HERE]'}

JOB DESCRIPTION:
${jobDesc || '[PASTE THE JOB DESCRIPTION HERE]'}

Please tell me:
1. My ATS match score (0-100) and what it means
2. The most important keywords in the job description that are missing from my resume
3. Which of my existing bullet points best match the role
4. My top 3 weakest bullet points and how to rewrite them to better match
5. One honest sentence about my overall fit for this role

Be direct and specific. I can handle honest feedback.`
    },
    tailor: {
      label: 'Tailor Resume to Role',
      sublabel: 'Get specific rewrites for this exact job',
      prompt: `You are a senior recruiter helping me tailor my resume for a specific role.

MY TARGET ROLE: ${targetTitle}

RESUME:
${resume || '[PASTE YOUR RESUME HERE]'}

JOB DESCRIPTION:
${jobDesc || '[PASTE THE JOB DESCRIPTION HERE]'}

Please:
1. Rewrite my top 5 bullet points to better match this specific role — keep them honest and accurate, just better framed
2. Suggest a stronger headline for this application
3. Identify any experience I have that I'm underselling for this role
4. Tell me the one thing most likely to get me screened out by a recruiter reviewing this`
    },
    gaps: {
      label: 'Identify Gaps',
      sublabel: 'Honest assessment of what\'s missing',
      prompt: `You are a senior recruiter giving honest feedback on a job application.

MY TARGET ROLE: ${targetTitle}

RESUME:
${resume || '[PASTE YOUR RESUME HERE]'}

JOB DESCRIPTION:
${jobDesc || '[PASTE THE JOB DESCRIPTION HERE]'}

Please give me an honest assessment:
1. What qualifications does this role require that I clearly have?
2. What qualifications does this role require that I'm missing?
3. What qualifications am I borderline on — present but not strong enough?
4. Should I apply for this role? Be honest.
5. If there are gaps, what could I do to address them before applying?`
    },
    summary: {
      label: 'Write a Resume Summary',
      sublabel: 'A strong opening summary tailored to your target role',
      prompt: `You are a senior recruiter and resume writer. Please write a strong professional summary for my resume.

MY TARGET ROLE: ${targetTitle}
MY BACKGROUND:
${resume || '[PASTE YOUR RESUME HERE]'}

Write 3 versions of a professional summary:
1. A punchy 2-sentence version for the top of my resume
2. A fuller 4-sentence version that covers breadth of experience
3. A version specifically tailored to a recruiter searching for ${targetTitle}

Keep them in first person, authentic, keyword-rich, and specific. No generic phrases like "results-driven professional."`
    },
    cold: {
      label: 'Cold Apply Cover Letter',
      sublabel: 'A cover letter for when you have no warm path',
      prompt: `You are a senior recruiter helping me write a cover letter for a cold application.

MY TARGET ROLE: ${targetTitle}

RESUME:
${resume || '[PASTE YOUR RESUME HERE]'}

JOB DESCRIPTION:
${jobDesc || '[PASTE THE JOB DESCRIPTION HERE]'}

Write a cover letter that:
1. Opens with something specific about this company — not generic praise
2. Makes a clear case for why my background matches this specific role
3. Addresses the most important requirement in the job description directly
4. Is under 250 words — recruiters don't read long cover letters
5. Ends with a specific ask, not "I hope to hear from you"

Write in my voice — professional but human, not corporate.`
    },
  }

  const tabs = Object.entries(PROMPTS)

  return (
    <div>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#00712D', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.85rem' }}>
          ← Back to My Search
        </button>
      )}
      <h2 className="section-title">Resume Analyzer</h2>
      <p style={{ color: '#555', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: '1.25rem' }}>
        Paste your resume and a job description below. Then copy the right prompt into Claude.ai or ChatGPT to get instant feedback based on how recruiters actually read resumes.
      </p>

      {/* Input fields */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A1A1A', display: 'block', marginBottom: '0.4rem' }}>
          Your resume
        </label>
        <textarea
          className="input-field"
          rows={5}
          placeholder="Paste your full resume text here..."
          value={resume}
          onChange={e => setResume(e.target.value)}
          style={{ resize: 'vertical', fontSize: '0.82rem' }}
        />
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A1A1A', display: 'block', marginBottom: '0.4rem' }}>
          Job description <span style={{ color: '#aaa', fontWeight: 400 }}>(optional for some prompts)</span>
        </label>
        <textarea
          className="input-field"
          rows={4}
          placeholder="Paste the job description you want to match..."
          value={jobDesc}
          onChange={e => setJobDesc(e.target.value)}
          style={{ resize: 'vertical', fontSize: '0.82rem' }}
        />
      </div>

      {/* Prompt selector tabs */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
          What do you need?
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {tabs.map(([key, p]) => (
            <button
              key={key}
              onClick={() => setActivePrompt(key)}
              style={{
                padding: '0.75rem 1rem',
                border: `2px solid ${activePrompt === key ? '#00712D' : '#e8e8e8'}`,
                borderRadius: '8px',
                background: activePrompt === key ? '#E8F2EC' : '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: '0.85rem', fontWeight: activePrompt === key ? 700 : 600, color: activePrompt === key ? '#00712D' : '#1A1A1A' }}>
                {p.label}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.1rem' }}>{p.sublabel}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Active prompt */}
      <CopyPromptBlock
        label={PROMPTS[activePrompt].label}
        sublabel="Copy this prompt → paste into Claude.ai or ChatGPT → paste your resume and job description where indicated"
        prompt={PROMPTS[activePrompt].prompt}
      />

      {/* How it works */}
      <div className="card" style={{ background: '#f9f9f9' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>How to use this</div>
        {[
          'Paste your resume and job description in the fields above',
          'Choose what kind of feedback you need',
          'Copy the prompt — it automatically includes your resume and job description',
          'Open Claude.ai or ChatGPT and paste the prompt',
          'Get recruiter-level feedback in seconds',
        ].map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.65rem', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#00712D', color: '#fff', fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '0.1rem' }}>{i + 1}</div>
            <div style={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.5 }}>{step}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
